import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  Stack,
  HStack,
  Button,
  Center,
  Flex,
  Heading,
  Spinner,
  useToast,
} from "@chakra-ui/react";
import Form from "@rjsf/chakra-ui";
import validator from "@rjsf/validator-ajv8";
import {
  reverseTransformData,
  transformData,
  transformOptions,
} from "../../util/helper";
import {
  createNewProject,
  getProjectData,
  updateProjectData,
} from "../../services/Projects";
import { getResearchCategoriesAPI } from "../../services/Films";
import ImageWidget from "../../CommonComponents/ImageWidget";
import TypeaheadWidget from "../../CommonComponents/TypeaheadWidget";
import { showErrorToast, showSuccessToast } from "../../util/errorHandler";

// Left Column Schema - Basic Fields
const leftColumnSchema = (alias) => ({
  title: "",
  description: "",
  type: "object",
  properties: {
    _id: {
      type: "string",
      title: "_id",
    },
    id: {
      type: "number",
      title: "Project Sequence Number",
      default: null,
    },
    title: {
      type: "string",
      title: "Title",
      default: "",
    },
    title_detail: {
      type: "string",
      title: "Title Detail",
      default: "",
    },
    alias: {
      type: "string",
      title: "Alias",
      default: "",
    },
    canto_cover_id: {
      type: "string",
      title: "Canto Cover ID",
      default: "",
    },
    display_date: {
      type: "string",
      title: "Display Date",
      default: "",
    },
    link: {
      type: "string",
      title: "Link",
      default: "",
    },
    published: {
      type: "boolean",
      title: "Published",
      default: false,
    },
  },
  required: ["title", "alias"],
});

// Right Column Schema - Categories & Keywords
const rightColumnSchema = () => ({
  title: "",
  description: "",
  type: "object",
  properties: {
    venue: {
      type: "string",
      title: "Venue",
      default: "",
    },
    short_title: {
      type: "string",
      title: "Short Title",
      default: "",
    },
    image: {
      type: "string",
      title: "Image",
      default: "",
    },
    preview_image: {
      type: "string",
      title: "Preview Image",
    },
    research_categories_alias: {
      type: "array",
      items: {
        type: "string",
      },
      title: "Research categories alias",
      default: [],
    },
    keywords: {
      type: "array",
      items: {
        type: "string",
      },
      title: "Keywords",
      default: [],
    },
  },
});

// Left Column UI Schema
const leftColumnUiSchema = (alias, isSubmitting) => ({
  "ui:submitButtonOptions": {
    norender: true,
  },
  "ui:style": {
    fontSize: "14px",
  },
  _id: {
    "ui:widget": "hidden",
  },
  id: {
    "ui:emptyValue": null,
    "ui:options": {
      inputType: "number",
    },
  },
  title: {
    "ui:emptyValue": "",
  },
  alias: {
    "ui:emptyValue": "",
    "ui:readonly": !!alias,
  },
  display_date: {
    "ui:emptyValue": "",
  },
  link: {
    "ui:emptyValue": "",
  },
  canto_cover_id: { "ui:emptyValue": "" },
});

// Right Column UI Schema
const rightColumnUiSchema = (isSubmitting) => ({
  "ui:submitButtonOptions": {
    norender: true,
  },
  "ui:style": {
    fontSize: "14px",
  },
  venue: {
    "ui:emptyValue": "",
  },
  short_title: {
    "ui:emptyValue": "",
    "ui:widget": "textarea",
  },
  image: {
    "ui:emptyValue": "",
  },
  preview_image: {
    "ui:widget": ImageWidget,
    "ui:emptyValue": "",
  },
  research_categories_alias: {
    items: {
      "ui:widget": "typeahead",
    },
    "ui:emptyValue": [],
  },
  keywords: {
    "ui:emptyValue": [],
  },
});

// Combined Schema for Single Column (Preview Mode)
const combinedSchema = (alias) => ({
  title: "",
  description: "",
  type: "object",
  properties: {
    _id: {
      type: "string",
      title: "_id",
    },
    id: {
      type: "number",
      title: "Project Sequence Number",
      default: null,
    },
    title: {
      type: "string",
      title: "Title",
      default: "",
    },
    title_detail: {
      type: "string",
      title: "Title Detail",
      default: "",
    },
    alias: {
      type: "string",
      title: "Alias",
      default: "",
    },
    canto_cover_id: {
      type: "string",
      title: "Canto Cover ID",
      default: "",
    },
    display_date: {
      type: "string",
      title: "Display Date",
      default: "",
    },
    link: {
      type: "string",
      title: "Link",
      default: "",
    },
    published: {
      type: "boolean",
      title: "Published",
      default: false,
    },
    venue: {
      type: "string",
      title: "Venue",
      default: "",
    },
    short_title: {
      type: "string",
      title: "Short Title",
      default: "",
    },
    image: {
      type: "string",
      title: "Image",
      default: "",
    },
    preview_image: {
      type: "string",
      title: "Image",
    },
    research_categories_alias: {
      type: "array",
      items: {
        type: "string",
      },
      title: "Research categories alias",
      default: [],
    },
    keywords: {
      type: "array",
      items: {
        type: "string",
      },
      title: "Keywords",
      default: [],
    },
  },
  required: ["title", "alias"],
});

// Combined UI Schema for Single Column (Preview Mode)
const combinedUiSchema = (alias, isSubmitting) => ({
  "ui:submitButtonOptions": {
    norender: true,
  },
  "ui:style": {
    fontSize: "14px",
  },
  _id: {
    "ui:widget": "hidden",
  },
  id: {
    "ui:emptyValue": null,
    "ui:options": {
      inputType: "number",
    },
  },
  title: {
    "ui:emptyValue": "",
  },
  alias: {
    "ui:emptyValue": "",
    "ui:readonly": !!alias,
  },
  display_date: {
    "ui:emptyValue": "",
  },
  link: {
    "ui:emptyValue": "",
  },
  venue: {
    "ui:emptyValue": "",
  },
  short_title: {
    "ui:emptyValue": "",
    "ui:widget": "textarea",
  },
  image: {
    "ui:emptyValue": "",
  },
  preview_image: {
    "ui:widget": ImageWidget,
    "ui:emptyValue": "",
  },
  canto_cover_id: { "ui:emptyValue": "" },
  research_categories_alias: {
    items: {
      "ui:widget": "typeahead",
    },
    "ui:emptyValue": [],
  },
  keywords: {
    "ui:emptyValue": [],
  },
});

const ProjectsPrimaryForm = () => {
  const { alias } = useParams();
  const [leftFormData, setLeftFormData] = useState({});
  const [rightFormData, setRightFormData] = useState({});
  const [combinedFormData, setCombinedFormData] = useState({});
  const [quickSave, setQuickSave] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();
  const previewEnabled = useSelector((state) => state.preview.isEnabled);

  // Split data for two-column layout
  const splitFormData = (data = {}) => {
    const leftFields = [
      "_id",
      "id",
      "title",
      "title_detail",
      "alias",
      "canto_cover_id",
      "display_date",
      "link",
      "published",
    ];
    const rightFields = [
      "venue",
      "short_title",
      "image",
      "preview_image",
      "research_categories_alias",
      "keywords",
    ];

    const leftData = {};
    const rightData = {};

    // Process left fields with defaults
    leftFields.forEach((field) => {
      if (data.hasOwnProperty(field)) {
        leftData[field] = data[field];
      } else {
        // Set appropriate defaults
        if (field === "published") leftData[field] = false;
        else if (field === "id") leftData[field] = null;
        else leftData[field] = "";
      }
    });

    // Process right fields with defaults
    rightFields.forEach((field) => {
      if (data.hasOwnProperty(field)) {
        rightData[field] = data[field];
      } else {
        // Set appropriate defaults
        if (field === "research_categories_alias" || field === "keywords") {
          rightData[field] = [];
        } else {
          rightData[field] = "";
        }
      }
    });

    return { leftData, rightData };
  };

  // Combine data from both forms
  const combineFormData = (leftData, rightData) => {
    return { ...leftData, ...rightData };
  };

  const widgets = {
    typeahead: (props) => (
      <TypeaheadWidget
        {...props}
        fetchOptions={(inputValue) => fetchCategories(inputValue, "alias")}
      />
    ),
  };

  const fetchCategories = async (inputValue, keyForLabelValue = "alias") => {
    const result = await getResearchCategoriesAPI();
    const transformedOptions = transformOptions(result, keyForLabelValue);

    // This function return the options
    return transformedOptions.filter((option) =>
      option.label.toLowerCase().includes(inputValue.toLowerCase()),
    );
  };

  // Handle form changes for two-column layout
  const handleLeftFormChange = (data) => {
    setLeftFormData(data.formData);
  };

  const handleRightFormChange = (data) => {
    setRightFormData(data.formData);
  };

  const handleCombinedFormChange = (data) => {
    setCombinedFormData(data.formData);
  };

  const dataWithImage = (data) => ({
    ...data,
    preview_image: data?.image,
  });

  // Fetch initial data
  useEffect(() => {
    const getBasicData = async () => {
      try {
        if (alias) {
          const result = await getProjectData(alias);
          if (result) {
            const updatedDataWithImage = dataWithImage(result?.data?.[0]);

            // To Show image on form need to provide url to image field which is only used to show the image
            const transformedData = transformData(updatedDataWithImage, []);

            // Split data for two-column layout
            const { leftData, rightData } = splitFormData(transformedData);
            setLeftFormData(leftData);
            setRightFormData(rightData);
            setCombinedFormData(transformedData);
          }
        } else {
          // For new projects, initialize empty form data
          const emptyData = {};
          const { leftData, rightData } = splitFormData(emptyData);
          setLeftFormData(leftData);
          setRightFormData(rightData);
          setCombinedFormData(emptyData);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        showErrorToast(toast, "Failed to fetch data");
      } finally {
        setIsLoading(false);
      }
    };

    getBasicData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alias, quickSave]);

  // Submit updated data
  const handleSubmit = async (formData = null) => {
    // Use combined form data in preview mode, otherwise combine left and right forms
    const finalFormData = previewEnabled
      ? combinedFormData
      : combineFormData(leftFormData, rightFormData);

    // Validate required fields
    if (!finalFormData.title || !finalFormData.alias) {
      return;
    }

    const updatedFormData = reverseTransformData(finalFormData, []);

    try {
      if (alias) {
        setIsSubmitting(true);
        const result = await updateProjectData(alias, updatedFormData);

        if (result?.success) {
          showSuccessToast(toast, "Data updated successfully");
          setQuickSave((prev) => !prev);
        }
      } else {
        const result = await createNewProject(updatedFormData); // Call the create API
        if (result?.success) {
          setQuickSave((prev) => !prev);
          showSuccessToast(toast, "New project created successfully");
          navigate(`/projects/${result?.data?.alias}`); // Navigate to the newly created post
        }
      }
    } catch (error) {
      console.error("Something went wrong data:", error);
      showErrorToast(toast, "Failed to fulfill request");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box maxW="auto">
      {isLoading ? (
        <Center h="400px">
          <Flex direction="column" align="center">
            <Spinner
              thickness="4px"
              speed="0.65s"
              emptyColor="gray.200"
              color="teal.500"
              size="xl"
              mb={4}
            />
            <Heading size="md" color="gray.600">
              Loading Project data...
            </Heading>
          </Flex>
        </Center>
      ) : (
        <Box>
          {previewEnabled ? (
            /* Single column layout for preview mode */
            <Box>
              <Form
                schema={combinedSchema(alias)}
                uiSchema={combinedUiSchema(alias, isSubmitting)}
                formData={combinedFormData}
                validator={validator}
                onChange={handleCombinedFormChange}
                widgets={widgets}
                fontsize={"14px"}
              />
            </Box>
          ) : (
            /* Two column layout with separate forms */
            <Box>
              <Stack flexDirection="row" gap={4} alignItems="flex-start">
                <HStack flex={1}>
                  <Box
                    width="100%"
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    p={4}
                    borderRadius="md"
                  >
                    <Box mb={4} fontWeight="bold" color="blue.800">
                      Column 1 - Basic Fields
                    </Box>
                    <Form
                      schema={leftColumnSchema(alias)}
                      uiSchema={leftColumnUiSchema(alias, isSubmitting)}
                      formData={leftFormData}
                      validator={validator}
                      onChange={handleLeftFormChange}
                      widgets={widgets}
                      fontsize={"14px"}
                    />
                  </Box>
                </HStack>
                <HStack flex={1}>
                  <Box
                    width="100%"
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    p={4}
                    borderRadius="md"
                  >
                    <Form
                      schema={rightColumnSchema()}
                      uiSchema={rightColumnUiSchema(isSubmitting)}
                      formData={rightFormData}
                      validator={validator}
                      onChange={handleRightFormChange}
                      widgets={widgets}
                      fontsize={"14px"}
                    />
                  </Box>
                </HStack>
              </Stack>
            </Box>
          )}
        </Box>
      )}

      {/* Fixed Save Button at Bottom */}
      <Box
        position="fixed"
        bottom={0}
        left="250px" // Account for sidebar width
        right={0}
        bg="white"
        borderTop="1px solid"
        borderColor="gray.200"
        p={4}
        zIndex={1000}
        display="flex"
      >
        <Box maxW="auto" display="flex" mx="auto">
          <Button
            colorScheme="blue"
            size="lg"
            width="auto"
            isLoading={isSubmitting}
            loadingText="Saving..."
            onClick={() => handleSubmit()}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Saving..." : "Save"}
          </Button>
        </Box>
      </Box>

      {/* Add padding to prevent content from being hidden behind fixed button */}
      <Box pb="100px" />
    </Box>
  );
};

export default ProjectsPrimaryForm;
